import { ReactNode, Suspense } from 'react';

import { Background, type BookingJourneyProps, BookingSheet, Navigation, Transition } from '@/ui';
import { AnnouncementBar, ButtonBlockProps, Footer } from '@/ui/server';
import { prependToNonHttpsHrefs } from '@lib/server';
import type {
  APIImageType,
  Animation,
  Announcement,
  BookingType,
  Default,
  NestedRepeaterLink,
  RepeaterLink,
  SocialWithContact,
  Theme,
} from '@types';
import { VenueBookingType } from '@types';

import { PlaceSchema } from '../util/place-schema';
import { NavigationSheet } from './navigation/navigation-sheet';

export function BrandSiteLayout({
  address,
  announcements,
  backgroundImage,
  backgroundImageOpacity,
  bookingComponents,
  bookingTypes,
  children,
  displayName,
  logo,
  venuesPageSlug,
  logoHref,
  navigationLinks,
  siteName,
  socials,
  navigationActions,
  footer,
  schema,
  transitionName,
  venue,
  theme,
}: {
  address?: string;
  schema: Default | undefined;
  announcements?: Announcement[];
  navigationActions: {
    primary: ButtonBlockProps;
    secondary: ButtonBlockProps;
  };
  backgroundImage?: APIImageType | Array<APIImageType>;
  backgroundImageOpacity?: string;
  bookingComponents: BookingJourneyProps['bookingComponents'];
  bookingTypes: (BookingType | VenueBookingType)[];
  children: ReactNode;
  displayName?: string;
  logo: APIImageType | Array<APIImageType>;
  logoHref?: string;
  venuesPageSlug: string;
  navigationLinks: NestedRepeaterLink[];
  siteName: string;
  theme: Theme | undefined;
  socials: SocialWithContact;
  transitionName?: Animation;
  venue?: string;
  footer: {
    operatorHref: string | undefined;
    operatorLogo: APIImageType | Array<APIImageType> | undefined;
    middleColumn: ReactNode;
    footerLinks: RepeaterLink[];
    subFooterLinks?: RepeaterLink[];
  };
}) {
  return (
    <>
      <div className="relative flex min-h-dvh flex-col items-center justify-between gap-8 overflow-clip sm:gap-20">
        <AnnouncementBar
          announcements={announcements
            ?.filter((a) => a?.content?.content)
            .map((a) => ({
              ...a,
              content: {
                content: venue
                  ? prependToNonHttpsHrefs(a.content?.content || '', `/${venuesPageSlug}/${venue}`) || ''
                  : a.content?.content || '',
              },
            }))}
        />

        {/* suspense added for search params */}
        <Suspense>
          <Navigation
            logo={logo}
            links={navigationLinks}
            siteName={siteName}
            socials={
              !navigationLinks?.length
                ? { phoneNumber: socials.phoneNumber, emailAddress: socials.emailAddress }
                : undefined
            }
            scrollThreshold={Boolean(announcements?.length) ? 48 : 0}
            subMenuImagePosition="right"
            variant={theme?.layout?.navigation}
            componentProps={{
              primaryButton: navigationActions?.primary,
              secondaryButton: navigationActions?.secondary,
            }}
            logoHref={logoHref}
          />
        </Suspense>

        <main className="flex w-full grow flex-col items-center gap-8 sm:gap-20">{children}</main>

        <Transition margin="0px" waitForInView transitionName={transitionName}>
          <Footer
            operatorLogo={footer.operatorLogo}
            operatorHref={footer.operatorHref}
            address={address}
            logo={logo}
            links={footer.footerLinks}
            variant={theme?.layout?.footer}
            socials={socials}
            displayName={displayName}
            siteName={siteName}
            policyLinks={footer.subFooterLinks}
            hideSiteName={!displayName}
            exitButton={Boolean(displayName)}
          >
            {footer.middleColumn}
          </Footer>
        </Transition>

        {backgroundImage &&
          ['sm', 'lg', 'md'].map((size) => (
            <Background
              key={size}
              image={backgroundImage}
              size={size as 'sm' | 'md' | 'lg'}
              opacity={backgroundImageOpacity}
            />
          ))}
      </div>

      {/* suspense added for search params */}
      <Suspense>
        <BookingSheet bookingTypes={bookingTypes} bookingComponents={bookingComponents} />
      </Suspense>

      {/* suspense added for search params */}
      <Suspense>
        <NavigationSheet socials={socials} logo={logo} links={navigationLinks} siteName={siteName} />
      </Suspense>

      {schema && <PlaceSchema data={schema} logo={logo} name={displayName || venue || siteName} brandName={siteName} />}
    </>
  );
}
